/*
 * @Description: axios
 * @Author: xiuwen
 * @Date: 2024-05-29 16:20:30
 * @LastEditors: xiuwen
 * @LastEditTime: 2026-07-20 15:26:19
 */
import axios from "axios";
import qs from "qs";

import showMessage from "~/utils/showMessage";
import { badjsReport } from "~/utils/errorReporting";
import { getChannel } from "~/utils/getChannel";
import { getRuntimeLocale } from "~/i18n/index";
import { resolveRequestLang } from "~/utils/request-lang";
import { isAuthRedirectExemptPath } from "~/utils/auth-redirect-routes";
import { stripLocalePrefix } from "~/utils/i18n-locale-routes";

const isSeerUserPage = () => {
	if (!import.meta.client || typeof window === "undefined") return false;
	return stripLocalePrefix(window.location.pathname) === "/seer/user";
};

const notifySeerUserAuthExpired = () => {
	if (!import.meta.client || typeof window === "undefined") return;
	window.dispatchEvent(new CustomEvent("seer-user-auth-expired"));
};

const isSilentBusinessError = (response, code) => {
	const requestUrl = response?.config?.url || "";
	// 日运：无档案（由业务页自行引导补档）
	if (requestUrl.includes("/v1/fate/new/new_day") && code === 1303) return true;
	// 月运：本年本月出生生日暂不支持（由业务页展示友好提示）
	if (requestUrl.includes("/v1/fate/new/new_month") && code === 400) return true;
	return false;
};

// 数据检查，预防违规域名
function checkData(response) {
	if (!import.meta.client) return;

	const { data, config } = response;

	let checkData = JSON.stringify(data);
	let regex = "",
		checkRes = "";
	const urlArr = [
		"tengzhihh.com", //谷歌被标记域名
		"sdklog.linghit.com", //谷歌被标记域名
		"error.youjyi.cn", //谷歌被标记域名
		"img-oss.suanfa666.com", //谷歌被标记域名
		"img-oss.lingwh.com", //因主体注销需下架域名
	];
	urlArr.forEach(v => {
		regex = new RegExp(v);
		checkRes = checkData.match(regex);
		if (checkRes && checkRes.length > 0) {
			let reportD = {
				api: config.url,
				warn: `检测到需下架域名:${v}，请更正`,
			};
			badjsReport({
				msg: JSON.stringify(reportD),
				from: typeof window !== "undefined" ? window.location.href : "",
				target: config.url,
			});
			console.error(`接口：${config.url} 检测到有使用被标记域名（${v}），请更正！！`);
		}
	});
}

function hasFile(data) {
	if (Object.prototype.toString.call(data) !== "[object Object]") return false;

	return Object.values(data).some(value => {
		return value instanceof FileList || value instanceof File || value instanceof Blob;
	});
}

export default defineNuxtPlugin(nuxtApp => {
	const localeCookie = useCookie("app-language");

	// 获取运行时配置
	const config = nuxtApp.$config.public;
	// 根据环境和运行时确定baseURL
	// 客户端使用代理（空字符串），服务端使用完整API地址
	let baseURL = config.apiBase;
	if (import.meta.client) {
		baseURL = "";
	}

	// 创建axios的一个实例
	const request = axios.create({
		baseURL: baseURL, // 接口统一域名
		timeout: 60000, // 设置超时
		headers: {
			"Content-Type": "application/x-www-form-urlencoded",
			Platform: "74",
		},
	});

	// 请求拦截器
	request.interceptors.request.use(
		config => {
			const { method, data } = config;

			let isFormData = o => !!Object.prototype.toString.call(o).match(/formdata/i);

			if (["post"].includes(config.method.toLowerCase()) && hasFile(config.data)) {
				const newData = new FormData();
				Object.keys(config.data).forEach(key => {
					newData.append(key, config.data[key]);
				});
				config.data = newData;
			} else {
				if (method.toLowerCase() === "post") {
					if (typeof data === "string" || isFormData(data)) {
						config.data = data;
					} else {
						// 检查是否明确设置了 Content-Type 为 application/json
						const contentType =
							config.headers?.["Content-Type"] || config.headers?.["content-type"];
						if (contentType === "application/json") {
							// 使用 JSON 格式
							config.data = JSON.stringify(data);
						} else {
							// 默认使用 URL 编码格式
							config.data = qs.stringify(data);
						}
					}
				}
			}

			// 请求未显式指定 Lang / lang 时，按当前 vue-i18n locale 解析（繁体 zh_TW，英文 en_US）
			if (!config.headers.Lang) {
				const path = import.meta.client
					? typeof window !== "undefined"
						? window.location.pathname
						: ""
					: nuxtApp.ssrContext?.url?.split("?")[0] || "";
				config.headers.Lang = resolveRequestLang(path, {
					i18nLocale: import.meta.client ? getRuntimeLocale() : localeCookie.value,
				});
			}
			if (!config.headers.lang) {
				config.headers.lang = config.headers.Lang;
			}

			if (!config.headers.timezone) {
				config.headers.timezone =
					Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Shanghai";
			}

			// 只在客户端处理token和channel
			if (import.meta.client) {
				/**
				 * 在线测试渠道，从cookies获取_channel
				 * web_subject项目channel: localStorage获取channel
				 */
				const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
				const channel = getChannel();
				const visitorId = typeof window !== "undefined" ? localStorage.getItem("USER_IDENT") : null;

				const explicitPlatform =
					config.headers?.Platform ?? config.headers?.platform ?? config.headers?.PLATFORM;
				if (explicitPlatform == null || explicitPlatform === "") {
					config.headers.platform = 74;
					config.headers.Platform = "74";
				}

				config.headers.Authorization = token;
				config.headers["Yqw-Token"] = token;
				config.headers.channel = channel;
				config.headers.visitor = visitorId;

				const appSystem = typeof window !== "undefined" ? localStorage.getItem("app_system") : null;

				// 如果 app 设置了 system，则使用
				if (appSystem) {
					config.headers.system = appSystem;
				}
			}

			return config;
		},
		error => {
			return Promise.reject(error);
		}
	);

	// 响应拦截器
	request.interceptors.response.use(
		response => {
			const { code } = response?.data;

			// 检测数据域名是否有违规域名
			checkData(response);

			if (code && code !== 1 && code !== 200) {
				if (isSilentBusinessError(response, code)) {
					return Promise.reject(response.data);
				}

				console.error(response.data);

				if (code === 1007 || code === 1010) {
					// 登录失效，清除用户信息
					if (import.meta.client) {
						localStorage.removeItem("token");
						localStorage.removeItem("gwUser");
						localStorage.removeItem("gw_token_phone");
						localStorage.removeItem("gw_token_email");
						if (isSeerUserPage()) {
							notifySeerUserAuthExpired();
							return Promise.reject(response.data);
						}
						// 公开落地页（如老師入駐）允许未登录浏览，失效 token 仅清本地态，不跳转首页
						if (isAuthRedirectExemptPath(window.location.pathname)) {
							return Promise.reject(response.data);
						}
						setTimeout(() => {
							navigateTo("/");
						}, 2000);
					}
				}

				showMessage(response?.data?.tips || response?.data?.msg, "error");
				return Promise.reject(response.data);
			}
			// 响应成功
			return response.data;
		},
		error => {
			let responseData;
			let requestConfig;
			let httpStatus;

			// 响应失败
			if (error.response) {
				const { data, config, status } = error.response;
				responseData = data;
				requestConfig = config;
				httpStatus = status;

				if (data?.code) {
					if ((data.code === 1007 || data.code === 1010) && isSeerUserPage()) {
						localStorage.removeItem("token");
						localStorage.removeItem("gwUser");
						localStorage.removeItem("gw_token_phone");
						localStorage.removeItem("gw_token_email");
						notifySeerUserAuthExpired();
						return Promise.reject(data);
					}
					showMessage(data.tips || data.msg || error.message, "error");
					return Promise.reject(error);
				}
			} else {
				showMessage(error.message || "未知错误", "error");
			}

			// 错误上报
			if (import.meta.client) {
				const requestData = requestConfig?.params || requestConfig?.data;
				badjsReport({
					msg: JSON.stringify({
						api: requestConfig?.url,
						status: httpStatus,
						requestData,
						responseData,
					}),
					from: typeof window !== "undefined" ? window.location.href : "",
					target: requestConfig?.url,
				});
			}

			return Promise.reject(error);
		}
	);

	// 注入到Nuxt
	nuxtApp.provide("axios", request);
});
